import {
  PricingModeEnum,
  SideEnum,
  Text,
  isDelta1Multileg,
  isOption,
  percentToBps,
  toBigWithDefault,
  type OrderAnalytic,
  type Security,
} from '@talos/kyoko';
import Big from 'big.js';

type VersusPxField = 'ArrivalPx' | 'MarketMidPx' | 'ReferencePx' | 'CumulativeVWAP' | 'CumulativeTWAP' | 'ExpectedPx';

export function computeSlippage({
  security,
  side,
  versusPxField,
  analyticOrLegAnalytic,
  parentAnalytic,
  pricingMode,
  isParentView,
}: {
  security: Security | undefined;
  side: SideEnum;
  versusPxField: keyof OrderAnalytic & VersusPxField;
  analyticOrLegAnalytic: Pick<OrderAnalytic, 'AvgPx' | VersusPxField>;
  parentAnalytic?: Pick<OrderAnalytic, 'AvgPx' | VersusPxField>;
  pricingMode?: PricingModeEnum;
  isParentView?: boolean;
}): string | null {
  const avgPx = analyticOrLegAnalytic.AvgPx;
  const versusPx = analyticOrLegAnalytic[versusPxField];
  if (avgPx == null || avgPx === '0' || versusPx == null || versusPx === '0') {
    return null;
  }

  if (isDelta1Multileg(security)) {
    if (!parentAnalytic) {
      return null;
    }
    const parentAvgPx = parentAnalytic?.AvgPx;
    const parentVersusPxBig = toBigWithDefault(parentAnalytic[versusPxField], 0);

    // For BPS-priced orders viewing parent analytics, values are already in BPS
    // so we just compute the difference without dividing by leg price.
    // Leg analytics always use the standard formula with division.
    if (pricingMode === PricingModeEnum.BPS && isParentView) {
      const slippageBps =
        side === SideEnum.Buy ? Big(parentAvgPx).sub(parentVersusPxBig) : Big(parentVersusPxBig).sub(parentAvgPx);
      return slippageBps.toFixed(2);
    }

    // For non-BPS orders (or leg analytics in BPS mode), parent values are in absolute USD terms
    // so we divide by leg price and convert to BPS
    if (side === SideEnum.Buy) {
      return percentToBps(Big(parentAvgPx).sub(parentVersusPxBig).div(versusPx));
    }
    return percentToBps(Big(parentVersusPxBig).sub(parentAvgPx).div(versusPx));
  }

  // https://talostrading.atlassian.net/browse/UI-3891
  if (isOption(security) && security?.BaseCurrency === security?.QuoteCurrency) {
    if (side === SideEnum.Buy) {
      return percentToBps(Big(avgPx).minus(versusPx));
    }
    return percentToBps(Big(versusPx).minus(avgPx));
  }

  if (side === SideEnum.Buy) {
    return percentToBps(Big(avgPx).sub(versusPx).div(versusPx));
  }
  return percentToBps(Big(versusPx).sub(avgPx).div(versusPx));
}

export function SlippageText({
  security,
  side,
  versusPxField,
  analyticOrLegAnalytic,
  parentAnalytic,
  pricingMode,
  isParentView,
}: {
  security: Security | undefined;
  side: SideEnum;
  versusPxField: keyof OrderAnalytic & VersusPxField;
  analyticOrLegAnalytic: Pick<OrderAnalytic, 'AvgPx' | VersusPxField>;
  parentAnalytic?: Pick<OrderAnalytic, 'AvgPx' | VersusPxField>;
  pricingMode?: PricingModeEnum;
  isParentView?: boolean;
}) {
  const bps = computeSlippage({
    security,
    side,
    versusPxField,
    analyticOrLegAnalytic,
    parentAnalytic,
    pricingMode,
    isParentView,
  });
  const negativeBps = Big(bps ?? 0).lt(0);
  const positiveBps = Big(bps ?? 0).gt(0);

  return <Text color={negativeBps ? 'green.lighten' : positiveBps ? 'red.lighten' : undefined}>{bps ?? 'N/A'}</Text>;
}
